
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Define o objeto gtag para evitar erros de TypeScript
declare global {
  interface Window {
    gtag: (
      command: 'event' | 'config' | 'set' | 'js', 
      action: any, 
      params?: any
    ) => void;
    dataLayer: any[];
  }
}

export type AnalyticsEventParams = {
  event_category?: string;
  event_label?: string;
  value?: number;
  currency?: string;
  transaction_id?: string;
  item_id?: string;
  item_name?: string;
  item_category?: string;
  price?: number;
  quantity?: number;
  coupon?: string;
  method?: string;
  user_type?: string;
  subscription_tier?: string;
  trial_duration?: number;
  [key: string]: any;
};

export function useAnalytics() {
  const location = useLocation();

  // Verifica se o usuário deu consentimento para cookies
  const hasConsent = () => {
    return localStorage.getItem('cookie-consent') === 'accepted';
  };

  // Rastrear mudanças de página
  useEffect(() => {
    if (typeof window.gtag !== 'undefined' && hasConsent()) {
      window.gtag('config', 'G-B4HYE28T75', {
        page_path: location.pathname + location.search
      });
      console.log('📊 Analytics: Page view', location.pathname);
    }
  }, [location]);

  // Função para enviar eventos personalizados
  const sendEvent = (
    eventName: string,
    params?: AnalyticsEventParams
  ) => {
    // Sempre envia eventos relacionados ao consentimento, mesmo sem consentimento prévio
    const isConsentRelated = eventName.includes('consent') || 
                            (params?.event_category && params.event_category.includes('consent'));
    
    if (typeof window.gtag !== 'undefined' && (hasConsent() || isConsentRelated)) {
      window.gtag('event', eventName, params);
      console.log('📊 Analytics: Event', eventName, params);
    } else {
      console.log('📊 Analytics: Event não enviado (sem consentimento)', eventName);
    }
  };

  // Enhanced Ecommerce Events
  const sendViewItemEvent = (itemId: string, itemName: string, category: string, value: number) => {
    sendEvent('view_item', {
      currency: 'EUR',
      value: value,
      items: [{
        item_id: itemId,
        item_name: itemName,
        item_category: category,
        price: value,
        quantity: 1
      }]
    });
  };

  const sendSelectItemEvent = (itemId: string, itemName: string, category: string) => {
    sendEvent('select_item', {
      item_list_id: 'pricing_plans',
      item_list_name: 'Pricing Plans',
      items: [{
        item_id: itemId,
        item_name: itemName,
        item_category: category
      }]
    });
  };

  const sendBeginCheckoutEvent = (itemId: string, itemName: string, value: number) => {
    sendEvent('begin_checkout', {
      currency: 'EUR',
      value: value,
      items: [{
        item_id: itemId,
        item_name: itemName,
        item_category: 'subscription',
        price: value,
        quantity: 1
      }]
    });
  };

  const sendPurchaseEvent = (
    transactionId: string,
    itemId: string,
    itemName: string,
    value: number,
    subscriptionTier: string,
    method: string = 'stripe'
  ) => {
    sendEvent('purchase', {
      transaction_id: transactionId,
      value: value,
      currency: 'EUR',
      method: method,
      subscription_tier: subscriptionTier,
      items: [{
        item_id: itemId,
        item_name: itemName,
        item_category: 'subscription',
        price: value,
        quantity: 1
      }]
    });
  };

  // User Events
  const sendSignUpEvent = (method: string, userType: string = 'trainer') => {
    sendEvent('sign_up', {
      method: method,
      user_type: userType,
      event_category: 'authentication'
    });
  };

  const sendLoginEvent = (method: string, userType: string = 'trainer') => {
    sendEvent('login', {
      method: method,
      user_type: userType,
      event_category: 'authentication'
    });
  };

  // Subscription Events
  const sendTrialStartEvent = (subscriptionTier: string, trialDuration: number = 15) => {
    sendEvent('subscription_trial_started', {
      subscription_tier: subscriptionTier,
      trial_duration: trialDuration,
      event_category: 'subscription',
      value: 0,
      currency: 'EUR'
    });
  };

  const sendTrialConversionEvent = (
    fromTier: string,
    toTier: string,
    value: number,
    transactionId: string
  ) => {
    sendEvent('trial_conversion', {
      subscription_tier: toTier,
      previous_tier: fromTier,
      value: value,
      currency: 'EUR',
      transaction_id: transactionId,
      event_category: 'subscription'
    });
  };

  const sendSubscriptionCancelEvent = (subscriptionTier: string, reason?: string) => {
    sendEvent('subscription_canceled', {
      subscription_tier: subscriptionTier,
      cancellation_reason: reason,
      event_category: 'subscription'
    });
  };

  const sendSubscriptionUpgradeEvent = (
    fromTier: string,
    toTier: string,
    value: number,
    transactionId: string
  ) => {
    sendEvent('subscription_upgrade', {
      subscription_tier: toTier,
      previous_tier: fromTier,
      value: value,
      currency: 'EUR',
      transaction_id: transactionId,
      event_category: 'subscription'
    });
  };

  const sendSubscriptionRenewalEvent = (
    subscriptionTier: string,
    value: number,
    transactionId: string
  ) => {
    sendEvent('subscription_renewal', {
      subscription_tier: subscriptionTier,
      value: value,
      currency: 'EUR',
      transaction_id: transactionId,
      event_category: 'subscription'
    });
  };

  // Engagement Events
  const sendFormInteractionEvent = (formName: string, action: string) => {
    sendEvent('form_interaction', {
      form_name: formName,
      interaction_type: action,
      event_category: 'engagement'
    });
  };

  const sendFeatureUsageEvent = (featureName: string, userType: string) => {
    sendEvent('feature_usage', {
      feature_name: featureName,
      user_type: userType,
      event_category: 'feature_usage'
    });
  };

  const sendContentDownloadEvent = (contentType: string, contentName: string) => {
    sendEvent('content_download', {
      content_type: contentType,
      content_name: contentName,
      event_category: 'engagement'
    });
  };

  const sendSocialShareEvent = (platform: string, contentType: string) => {
    sendEvent('share', {
      method: platform,
      content_type: contentType,
      event_category: 'social'
    });
  };

  // Cookie Consent Events
  const sendCookieConsentEvent = (action: 'accepted' | 'declined') => {
    sendEvent(`cookie_consent_${action}`, {
      event_category: 'consent',
      consent_type: 'cookies'
    });
  };

  // Workout Generation Event
  const sendWorkoutGeneratedEvent = (workoutType: string, difficulty: string, duration: number) => {
    sendEvent('workout_generated', {
      event_category: 'feature_usage',
      workout_type: workoutType,
      workout_difficulty: difficulty,
      workout_duration: duration
    });
  };

  return { 
    sendEvent, 
    hasConsent,
    // Enhanced Ecommerce
    sendViewItemEvent,
    sendSelectItemEvent,
    sendBeginCheckoutEvent,
    sendPurchaseEvent,
    // User Events
    sendSignUpEvent,
    sendLoginEvent,
    // Subscription Events
    sendTrialStartEvent,
    sendTrialConversionEvent,
    sendSubscriptionCancelEvent,
    sendSubscriptionUpgradeEvent,
    sendSubscriptionRenewalEvent,
    // Engagement Events
    sendFormInteractionEvent,
    sendFeatureUsageEvent,
    sendContentDownloadEvent,
    sendSocialShareEvent,
    sendWorkoutGeneratedEvent,
    // Cookie Consent
    sendCookieConsentEvent
  };
}

// Eventos comuns pré-definidos - OTIMIZADOS PARA FACEBOOK
export const AnalyticsEvents = {
  // Eventos de usuário
  USER_SIGNUP: 'sign_up',
  USER_LOGIN: 'login',
  USER_LOGOUT: 'logout',
  
  // Enhanced Ecommerce (Facebook Standard Events)
  VIEW_ITEM: 'view_item',
  SELECT_ITEM: 'select_item',
  BEGIN_CHECKOUT: 'begin_checkout',
  PURCHASE: 'purchase',
  
  // Eventos de assinatura
  SUBSCRIPTION_TRIAL_STARTED: 'subscription_trial_started',
  TRIAL_CONVERSION: 'trial_conversion',
  SUBSCRIPTION_UPGRADE: 'subscription_upgrade',
  SUBSCRIPTION_RENEWAL: 'subscription_renewal',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  SUBSCRIPTION_PURCHASED: 'subscription_purchased', // Adicionado
  
  // Eventos de engagement
  FORM_INTERACTION: 'form_interaction',
  FEATURE_USAGE: 'feature_usage',
  CONTENT_DOWNLOAD: 'content_download',
  SOCIAL_SHARE: 'share',
  WORKOUT_GENERATED: 'workout_generated', // Adicionado
  
  // Eventos de cookies
  COOKIE_CONSENT_ACCEPTED: 'cookie_consent_accepted',
  COOKIE_CONSENT_DECLINED: 'cookie_consent_declined',
};

// Mapeamento para Facebook Standard Events (para configuração manual)
export const FacebookEventMapping = {
  // Standard Events do Facebook
  PURCHASE: 'Purchase',
  INITIATE_CHECKOUT: 'InitiateCheckout', 
  LEAD: 'Lead',
  COMPLETE_REGISTRATION: 'CompleteRegistration',
  START_TRIAL: 'StartTrial',
  VIEW_CONTENT: 'ViewContent',
  SUBSCRIBE: 'Subscribe',
  
  // Custom Events para configuração manual
  TRIAL_CONVERSION: 'TrialConversion',
  SUBSCRIPTION_RENEWAL: 'SubscriptionRenewal',
  WORKOUT_GENERATED: 'WorkoutGenerated',
  FEATURE_USAGE: 'FeatureUsage'
};

